import React from 'react';
import { Element } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Input } from '@/ui-components/ui/input';
import { Button } from '@/ui-components/ui/button';
import { Slider } from '@/ui-components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';
import { Plus, Sun, Droplets, Settings2, Trash2 } from 'lucide-react';
import { AdvancedStaticDynamicSelector } from '../content-selector/AdvancedStaticDynamicSelector';

interface EffectsSectionProps {
  element: Element;
  onOpenSlotPanel?: () => void;
}

type EffectType = 'drop-shadow' | 'layer-blur';

export const EffectsSection: React.FC<EffectsSectionProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);
  const effects = element.effects || [];

  const handleAddEffect = (type: EffectType) => {
    // Check if effect type already exists
    if (effects.some((e: any) => e.type === type)) {
      return;
    }

    const newEffect = type === 'drop-shadow' 
      ? {
          id: crypto.randomUUID(),
          type: 'drop-shadow' as const,
          x: 0,
          y: 4,
          blur: 4,
          color: { mode: 'static' as const, mediaType: 'color' as const, staticValue: '#000000' },
          opacity: 0.25
        }
      : {
          id: crypto.randomUUID(),
          type: 'layer-blur' as const,
          radius: 4
        };
    
    // @ts-ignore - Schema validation happens elsewhere, simplified creation here
    updateElement(element.id, { effects: [...effects, newEffect] });
  };

  const handleRemoveEffect = (id: string) => {
    updateElement(element.id, { effects: effects.filter((e: any) => e.id !== id) });
  };

  const handleUpdateEffect = (id: string, updates: Partial<any>) => {
    updateElement(element.id, {
      effects: effects.map((e: any) => e.id === id ? { ...e, ...updates } : e)
    });
  };

  const hasDropShadow = effects.some((e: any) => e.type === 'drop-shadow');
  const hasLayerBlur = effects.some((e: any) => e.type === 'layer-blur');

  return (
    <div className="space-y-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sun className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Effekte</Label>
        </div>
        
        {/* Add Effect Dropdown */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md hover:bg-gray-100 border border-gray-200" disabled={hasDropShadow && hasLayerBlur}>
              <Plus className="w-3.5 h-3.5 text-gray-600" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-40 p-1.5 rounded-xl shadow-lg border-gray-100">
            <div className="space-y-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-start text-[11px] h-8 px-2 rounded-lg gap-2"
                onClick={() => handleAddEffect('drop-shadow')}
                disabled={hasDropShadow}
              >
                <Sun className="w-3 h-3 text-gray-400" />
                Schatten
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-start text-[11px] h-8 px-2 rounded-lg gap-2"
                onClick={() => handleAddEffect('layer-blur')}
                disabled={hasLayerBlur}
              >
                <Droplets className="w-3 h-3 text-gray-400" />
                Weichzeichner
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Effects List */}
      <div className="space-y-2">
        {effects.map((effect: any) => (
          <div key={effect.id} className="group flex items-center justify-between rounded-xl border border-gray-200 p-2 bg-white hover:bg-gray-50/50 transition-all shadow-sm hover:border-gray-300">
            <div className="flex items-center gap-2.5 pl-1">
              <div className="p-1.5 rounded-lg bg-gray-50 text-gray-500">
                {effect.type === 'drop-shadow' ? (
                  <Sun className="w-3.5 h-3.5" />
                ) : (
                  <Droplets className="w-3.5 h-3.5" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] font-bold text-gray-800 leading-tight">
                  {effect.type === 'drop-shadow' ? 'Schatten' : 'Weichzeichner'}
                </span>
                <span className="text-[9px] text-gray-400 font-medium">
                  {effect.type === 'drop-shadow' ? `${effect.x}, ${effect.y} · ${effect.blur}px` : `${effect.radius}px`}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Settings Trigger */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                    <Settings2 className="w-3.5 h-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="left" align="start" className="w-64 p-4 z-50 rounded-2xl shadow-xl border-gray-100">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2 mb-1">
                      <div className="flex items-center gap-2">
                        {effect.type === 'drop-shadow' ? <Sun className="w-3.5 h-3.5 text-blue-600" /> : <Droplets className="w-3.5 h-3.5 text-blue-600" />}
                        <Label className="text-[12px] font-bold text-gray-800">
                          {effect.type === 'drop-shadow' ? 'Schatten' : 'Weichzeichner'}
                        </Label>
                      </div>
                    </div>

                    {effect.type === 'drop-shadow' && (
                      <div className="space-y-4">
                         {/* Position */}
                         <div className="grid grid-cols-2 gap-3">
                           <div className="space-y-1.5">
                             <Label className="text-[11px] text-gray-500 font-medium">Versatz X</Label>
                             <Input 
                               type="number" 
                               value={Math.round(effect.x)} 
                               onChange={(e) => handleUpdateEffect(effect.id, { x: parseFloat(e.target.value) || 0 })}
                               className="h-8 text-[11px] rounded-lg border-gray-200"
                             />
                           </div>
                           <div className="space-y-1.5">
                             <Label className="text-[11px] text-gray-500 font-medium">Versatz Y</Label>
                             <Input 
                               type="number" 
                               value={Math.round(effect.y)} 
                               onChange={(e) => handleUpdateEffect(effect.id, { y: parseFloat(e.target.value) || 0 })}
                               className="h-8 text-[11px] rounded-lg border-gray-200"
                             />
                           </div>
                         </div>

                         {/* Blur */}
                         <div className="space-y-2">
                           <div className="flex justify-between items-center">
                            <Label className="text-[11px] text-gray-500 font-medium">Weichheit</Label>
                            <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">{Math.round(effect.blur)}px</span>
                           </div>
                           <Slider
                             value={[effect.blur]} 
                             onValueChange={([v]) => handleUpdateEffect(effect.id, { blur: v })}
                             max={50}
                             step={1}
                           />
                         </div>

                         {/* Color */}
                         <div className="pt-1">
                            <AdvancedStaticDynamicSelector
                              label="Farbe"
                              value={effect.color || { mode: 'static', mediaType: 'color', staticValue: '#000000' }}
                              onChange={(value: any) => handleUpdateEffect(effect.id, { color: value })}
                              compatibleMediaTypes={["color"]}
                              required={true}
                              onOpenSlotPanel={onOpenSlotPanel}
                            />
                         </div>

                         {/* Opacity */}
                         <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <Label className="text-[11px] text-gray-500 font-medium">Deckkraft</Label>
                                <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">{Math.round((effect.opacity ?? 0.5) * 100)}%</span>
                            </div>
                            <Slider
                              value={[(effect.opacity ?? 0.5) * 100]}
                              onValueChange={([v]) => handleUpdateEffect(effect.id, { opacity: v / 100 })}
                              max={100}
                              step={1}
                            />
                         </div>
                      </div>
                    )}

                    {effect.type === 'layer-blur' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                           <div className="flex justify-between items-center">
                            <Label className="text-[11px] text-gray-500 font-medium">Radius</Label>
                            <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-bold">{Math.round(effect.radius)}px</span>
                           </div>
                           <Slider
                             value={[effect.radius]} 
                             onValueChange={([v]) => handleUpdateEffect(effect.id, { radius: v })}
                             max={50}
                             step={1}
                           />
                         </div>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Remove Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                onClick={() => handleRemoveEffect(effect.id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        
        {effects.length === 0 && (
          <div className="text-center py-6 px-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
            <Sun className="w-5 h-5 text-gray-300 mx-auto mb-2" />
            <p className="text-[11px] text-gray-400 font-medium">Keine Effekte aktiv</p>
          </div>
        )}
      </div>
    </div>
  );
};
